import { useLayout } from '@celito.clients/hooks';
import {
  UserContext,
  useTenantRegionalSettings,
} from '@celito.clients/provider';
import { updateTenantRegionalSettings } from '@celito.clients/services';
import {
  formatDate,
  raiseErrorToast,
  successToast,
} from '@celito.clients/utils';
import {
  Button,
  Combobox,
  Divider,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  CalendarRegular,
  ClockRegular,
  GlobeRegular,
} from '@fluentui/react-icons';
import { useContext, useEffect, useMemo, useState } from 'react';

import styles from './tenant-settings.module.css';

const DATE_FORMATS = [
  { value: 'd MMM yyyy', example: '17 Apr 2026' },
  { value: 'yyyy-MM-dd', example: '2026-04-17' },
  { value: 'MM/dd/yyyy', example: '04/17/2026' },
];

const TIME_FORMATS = [
  { value: 'HH:mm', label: '24-hour', example: '14:30' },
  { value: 'h:mm a', label: '12-hour', example: '2:30 PM' },
  { value: 'HH:mm:ss', label: '24-hour with seconds', example: '14:30:45' },
  { value: 'h:mm:ss a', label: '12-hour with seconds', example: '2:30:45 PM' },
];

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Guayaquil',
  'America/Los_Angeles',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Hong_Kong',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Pacific/Auckland',
  'Pacific/Honolulu',
];

interface TimezoneOption {
  value: string;
  label: string;
}

const PREFERRED_TIMEZONES: TimezoneOption[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'America/Halifax', label: 'Atlantic Time (Canada)' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (Canada)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'Europe/Dublin', label: 'Ireland (Dublin)' },
  { value: 'Europe/Lisbon', label: 'Western European (Lisbon)' },
  { value: 'Europe/Paris', label: 'Central European (Paris, Madrid, Rome)' },
  { value: 'Europe/Berlin', label: 'Central European (Berlin, Amsterdam)' },
  { value: 'Europe/Stockholm', label: 'Central European (Stockholm, Warsaw)' },
  { value: 'Europe/Athens', label: 'Eastern European (Athens, Helsinki)' },
  { value: 'Europe/Moscow', label: 'Moscow Time' },
];

const TIMEZONE_LABELS = new Map(
  PREFERRED_TIMEZONES.map(({ value, label }) => [value, label])
);

const timezoneLabel = (timezone: string): string =>
  TIMEZONE_LABELS.get(timezone) ?? timezone;

const getTimezones = (): TimezoneOption[] => {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  const supported = intl.supportedValuesOf?.('timeZone');
  const timezones = supported
    ? Array.from(new Set(['UTC', ...supported]))
    : FALLBACK_TIMEZONES;
  const preferredValues = new Set(
    PREFERRED_TIMEZONES.map(({ value }) => value)
  );

  return [
    ...PREFERRED_TIMEZONES.filter(({ value }) => timezones.includes(value)),
    ...timezones
      .filter((timezone) => !preferredValues.has(timezone))
      .sort()
      .map((timezone) => ({ value: timezone, label: timezone })),
  ];
};

const TIMEZONE_OPTIONS = getTimezones();

const TenantSettingsScreen = () => {
  const { configureLayout } = useLayout();
  const { user, isAdmin } = useContext(UserContext);
  const { settings, applySettings } = useTenantRegionalSettings();
  const [draft, setDraft] = useState(settings);
  const [timezoneQuery, setTimezoneQuery] = useState(
    timezoneLabel(settings.timezone)
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(settings);
    setTimezoneQuery(timezoneLabel(settings.timezone));
    configureLayout({
      pageTitle: '',
    });
  }, [settings]);

  const timezoneOptions = useMemo(() => {
    const query = timezoneQuery.trim().toLowerCase();
    const selectedLabel = timezoneLabel(draft.timezone).toLowerCase();

    if (!query || query === selectedLabel) return TIMEZONE_OPTIONS;

    return TIMEZONE_OPTIONS.filter(
      ({ value, label }) =>
        value.toLowerCase().includes(query) ||
        label.toLowerCase().includes(query)
    );
  }, [draft.timezone, timezoneQuery]);

  const isDirty =
    draft.timezone !== settings.timezone ||
    draft.dateFormat !== settings.dateFormat ||
    draft.timeFormat !== settings.timeFormat;

  const preview = useMemo(() => {
    try {
      return formatDate(
        new Date(),
        `${draft.dateFormat} ${draft.timeFormat}`,
        draft.timezone
      );
    } catch {
      return 'Invalid regional settings';
    }
  }, [draft]);

  const cancel = () => {
    setDraft(settings);
    setTimezoneQuery(timezoneLabel(settings.timezone));
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const saved = await updateTenantRegionalSettings(draft);
      applySettings(saved);
      successToast({ message: 'Tenant settings saved.' });
    } catch (reason) {
      raiseErrorToast(reason);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return <Spinner label="Checking Tenant Settings access" />;

  return (
    <main className={styles.page}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (isDirty && !isSaving) void save();
        }}
      >
        <div className={styles.contentGrid}>
          <section
            className={styles.settingsSection}
            aria-labelledby="regional-preferences-heading"
          >
            <div className={styles.sectionHeading}>
              <GlobeRegular className={styles.sectionIcon} />
              <div>
                <Text
                  as="h2"
                  id="regional-preferences-heading"
                  size={500}
                  weight="semibold"
                >
                  Regional preferences
                </Text>
                <Text size={300}>
                  Choose how dates and times are displayed across Helyx.
                </Text>
              </div>
            </div>

            <div className={styles.formStack}>
              <Field
                label="Timezone"
                hint="Search by city, region, or IANA timezone."
              >
                <Combobox
                  value={timezoneQuery}
                  selectedOptions={[draft.timezone]}
                  onChange={(event) => setTimezoneQuery(event.target.value)}
                  onOptionSelect={(_, data) => {
                    if (!data.optionValue) return;
                    setDraft((current) => ({
                      ...current,
                      timezone: data.optionValue as string,
                    }));
                    setTimezoneQuery(timezoneLabel(data.optionValue as string));
                  }}
                  onBlur={() => setTimezoneQuery(timezoneLabel(draft.timezone))}
                  disabled={isSaving}
                >
                  {timezoneOptions.length ? (
                    timezoneOptions.map(({ value, label }) => (
                      <Option key={value} value={value} text={label}>
                        {label}
                      </Option>
                    ))
                  ) : (
                    <Option disabled>No timezones found</Option>
                  )}
                </Combobox>
              </Field>

              <Divider />

              <div className={styles.fieldGrid}>
                <Field label="Date format">
                  <Dropdown
                    value={
                      DATE_FORMATS.find(
                        ({ value }) => value === draft.dateFormat
                      )?.example
                    }
                    selectedOptions={[draft.dateFormat]}
                    onOptionSelect={(_, data) =>
                      data.optionValue &&
                      setDraft((current) => ({
                        ...current,
                        dateFormat: data.optionValue as string,
                      }))
                    }
                    disabled={isSaving}
                  >
                    {DATE_FORMATS.map(({ value, example }) => (
                      <Option
                        key={value}
                        value={value}
                        text={`${example} — ${value}`}
                      >
                        {example} — {value}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label="Time format">
                  <Dropdown
                    value={
                      TIME_FORMATS.find(
                        ({ value }) => value === draft.timeFormat
                      )?.label
                    }
                    selectedOptions={[draft.timeFormat]}
                    onOptionSelect={(_, data) =>
                      data.optionValue &&
                      setDraft((current) => ({
                        ...current,
                        timeFormat: data.optionValue as string,
                      }))
                    }
                    disabled={isSaving}
                  >
                    {TIME_FORMATS.map(({ value, label, example }) => (
                      <Option
                        key={value}
                        value={value}
                        text={`${label} — ${example}`}
                      >
                        {label} — {example}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>
            </div>
          </section>

          <aside className={styles.previewColumn}>
            <div className={styles.previewPanel}>
              <Text className={styles.eyebrow} weight="semibold">
                Preview
              </Text>
              <output
                className={styles.previewValue}
                aria-live="polite"
                aria-atomic="true"
              >
                {preview}
              </output>
              <Text className={styles.previewHint} size={300}>
                This example updates as you change the settings.
              </Text>

              <Divider />

              <dl className={styles.summaryList}>
                <div>
                  <dt>
                    <GlobeRegular /> Timezone
                  </dt>
                  <dd>{timezoneLabel(draft.timezone)}</dd>
                </div>
                <div>
                  <dt>
                    <CalendarRegular /> Date
                  </dt>
                  <dd>{draft.dateFormat}</dd>
                </div>
                <div>
                  <dt>
                    <ClockRegular /> Time
                  </dt>
                  <dd>{draft.timeFormat}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        <footer className={styles.actions}>
          <Text className={styles.changeStatus} size={300}>
            {isDirty ? 'You have unsaved changes.' : 'All changes are saved.'}
          </Text>
          <div className={styles.actionButtons}>
            <Button
              appearance="secondary"
              size="large"
              type="button"
              disabled={!isDirty || isSaving}
              onClick={cancel}
            >
              Discard changes
            </Button>
            <Button
              appearance="primary"
              size="large"
              type="submit"
              disabled={!isDirty || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </footer>
      </form>
    </main>
  );
};

export default TenantSettingsScreen;
